import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  Box,
  Typography,
  IconButton,
  Grid
} from "@mui/material";
import { CloudUpload, Delete } from "@mui/icons-material";
import { Award } from "../types/award";
import { useAddAwardMutation, useUpdateAwardMutation } from "../api/awardApi";
import { useGetAwardAreasQuery } from '../../awardareas/api/awardareaApi';
import { AwardArea } from '../../awardareas/types/awardarea';

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  award?: Award | null;
}

const parseArray = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      // If it's not valid JSON, treat it as a single string and split by comma
      return value.split(',').map((s: string) => s.trim()).filter(Boolean);
    }
  }
  return [];
};

const AddEditAward: React.FC<Props> = ({ open, setModalOpen, onSuccess, award }) => {
  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    year: string;
    status: string;
    criteria: string[];
    prizes: string[];
    nomination_deadline: string;
    ceremony_date: string;
    ceremony_location: string;
    nomination_period_start: string;
    nomination_period_end: string;
    voting_period_start: string;
    voting_period_end: string;
    is_active: boolean;
    area_ids: number[];
  }>({
    title: "",
    description: "",
    year: "",
    status: "upcoming",
    criteria: [],
    prizes: [],
    nomination_deadline: "",
    ceremony_date: "",
    ceremony_location: "",
    nomination_period_start: "",
    nomination_period_end: "",
    voting_period_start: "",
    voting_period_end: "",
    is_active: true,
    area_ids: [],
  });

  const [createAward] = useAddAwardMutation();
  const [updateAward] = useUpdateAwardMutation();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);

  const { data: awardAreasData } = useGetAwardAreasQuery();
  const awardAreas: AwardArea[] = awardAreasData?.data || [];

  useEffect(() => {
    if (award) {
      setFormData({
        title: award.title,
        description: award.description,
        year: award.year,
        status: award.status?.toLowerCase() || "upcoming",
        criteria: parseArray(award.criteria),
        prizes: parseArray(award.prizes),
        nomination_deadline: award.nomination_deadline,
        ceremony_date: award.ceremony_date,
        ceremony_location: award.ceremony_location,
        nomination_period_start: award.nomination_period_start || "",
        nomination_period_end: award.nomination_period_end || "",
        voting_period_start: award.voting_period_start || "",
        voting_period_end: award.voting_period_end || "",
        is_active: award.is_active,
        area_ids: award.areas ? award.areas.map(a => a.id) : [],
      });
      setExistingImages(parseArray(award.images));
      setImageFiles([]);
    } else {
      setFormData({
        title: "",
        description: "",
        year: "",
        status: "upcoming",
        criteria: [],
        prizes: [],
        nomination_deadline: "",
        ceremony_date: "",
        ceremony_location: "",
        nomination_period_start: "",
        nomination_period_end: "",
        voting_period_start: "",
        voting_period_end: "",
        is_active: true,
        area_ids: [],
      });
      setExistingImages([]);
      setImageFiles([]);
    }
  }, [award, open]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);
      setImageFiles([...imageFiles, ...newFiles]);
    }
  };

  const handleRemoveNewImage = (index: number) => {
    setImageFiles(imageFiles.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = (index: number) => {
    setExistingImages(existingImages.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const errors: { [key: string]: string } = {};
    if (!formData.title.trim()) {
      errors.title = "Title is required.";
    }
    if (!formData.description.trim()) {
      errors.description = "Description is required.";
    }
    if (!formData.year.trim()) {
      errors.year = "Year is required.";
    }
    if (!formData.status.trim()) {
      errors.status = "Status is required.";
    }
    if (!formData.criteria || !Array.isArray(formData.criteria) || formData.criteria.length === 0) {
      errors.criteria = "Please provide at least one criteria.";
    }
    if (!formData.prizes || !Array.isArray(formData.prizes) || formData.prizes.length === 0) {
      errors.prizes = "Please provide at least one prize.";
    }
    if (!formData.nomination_deadline.trim()) {
      errors.nomination_deadline = "Nomination deadline is required.";
    }
    if (!formData.ceremony_date.trim()) {
      errors.ceremony_date = "Ceremony date is required.";
    }
    if (!formData.ceremony_location.trim()) {
      errors.ceremony_location = "Ceremony location is required.";
    }
    if (!formData.nomination_period_start.trim()) {
      errors.nomination_period_start = "Nomination period start is required.";
    }
    if (!formData.nomination_period_end.trim()) {
      errors.nomination_period_end = "Nomination period end is required.";
    }
    if (!formData.voting_period_start.trim()) {
      errors.voting_period_start = "Voting period start is required.";
    }
    if (!formData.voting_period_end.trim()) {
      errors.voting_period_end = "Voting period end is required.";
    }
    if (!formData.area_ids || formData.area_ids.length === 0) {
      errors.area_ids = "Please select at least one award area.";
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setSubmissionError(Object.values(errors)[0]);
      return;
    }
    setIsSubmitting(true);
    
    try {
      // Use FormData when editing (to handle existing images) or when there are new images
      const hasNewImages = imageFiles.length > 0;
      const isEditing = award?.id !== undefined;
      const useFormData = hasNewImages || (isEditing && existingImages.length > 0);
      
      if (useFormData) {
        const formDataToSend = new FormData();
        formDataToSend.append('title', formData.title);
        formDataToSend.append('description', formData.description);
        formDataToSend.append('year', formData.year);
        formDataToSend.append('status', formData.status.toLowerCase());
        
        // Append criteria as array items
        (formData.criteria ?? []).forEach((item) => {
          formDataToSend.append('criteria[]', item);
        });
        
        // Append prizes as array items
        (formData.prizes ?? []).forEach((item) => {
          formDataToSend.append('prizes[]', item);
        });
        
        formDataToSend.append('nomination_deadline', formData.nomination_deadline);
        formDataToSend.append('ceremony_date', formData.ceremony_date);
        formDataToSend.append('ceremony_location', formData.ceremony_location);
        formDataToSend.append('nomination_period_start', formData.nomination_period_start);
        formDataToSend.append('nomination_period_end', formData.nomination_period_end);
        formDataToSend.append('voting_period_start', formData.voting_period_start);
        formDataToSend.append('voting_period_end', formData.voting_period_end);
        // Send is_active as "1" or "0" which Laravel accepts for boolean validation
        formDataToSend.append('is_active', formData.is_active ? '1' : '0');
        
        // Append area_ids as array items
        formData.area_ids.forEach((id) => {
          formDataToSend.append('area_ids[]', id.toString());
        });
        
        // Append existing images that weren't removed (only when editing)
        if (isEditing && existingImages.length > 0) {
          existingImages.forEach((image) => {
            formDataToSend.append('existing_images[]', image);
          });
        }
        
        // Append new image files (only if there are new images)
        if (hasNewImages) {
          imageFiles.forEach((file) => {
            formDataToSend.append('images[]', file);
          });
        }

        if (award?.id) {
          await updateAward({ id: award.id, body: formDataToSend }).unwrap();
        } else {
          await createAward({ body: formDataToSend }).unwrap();
        }
      } else {
        const body = {
          ...formData,
          status: formData.status.toLowerCase(),
          criteria: formData.criteria ?? [],
          prizes: formData.prizes ?? [],
          images: existingImages,
        };
        
        if (award?.id) {
          await updateAward({ id: award.id, body }).unwrap();
        } else {
          await createAward({ body }).unwrap();
        }
      }
      setModalOpen(false);
      onSuccess?.();
      setSubmissionError(null);
      setFieldErrors({});
    } catch (err) {
      setSubmissionError("Failed to save award. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseError = () => {
    setSubmissionError(null);
  };

  return (
    <Dialog open={open} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{award ? "Edit Award" : "Add Award"}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="dense"
          label="Title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          error={!!fieldErrors.title}
          helperText={fieldErrors.title}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Description"
          multiline
          rows={3}
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          error={!!fieldErrors.description}
          helperText={fieldErrors.description}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Year"
          value={formData.year}
          onChange={(e) => setFormData({ ...formData, year: e.target.value })}
          error={!!fieldErrors.year}
          helperText={fieldErrors.year}
        />
        <FormControl fullWidth margin="dense" error={!!fieldErrors.status}>
          <InputLabel id="status-label">Status</InputLabel>
          <Select
            labelId="status-label"
            value={formData.status?.toLowerCase() || "upcoming"}
            onChange={(e) => setFormData({ ...formData, status: e.target.value.toLowerCase() })}
            label="Status"
          >
            <MenuItem value="upcoming">Upcoming</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="active">Active</MenuItem>
          </Select>
          {fieldErrors.status && (
            <p style={{ color: '#d32f2f', margin: '3px 14px 0 14px', fontSize: '0.75rem' }}>{fieldErrors.status}</p>
          )}
        </FormControl>
        <TextField
          fullWidth
          label="Criteria (comma separated)"
          value={Array.isArray(formData.criteria) ? formData.criteria.join(", ") : ""}
          onChange={(e) => setFormData({ ...formData, criteria: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
          sx={{ mt: 2 }}
          error={!!fieldErrors.criteria}
          helperText={fieldErrors.criteria}
        />
        <TextField
          fullWidth
          label="Prizes (comma separated)"
          value={Array.isArray(formData.prizes) ? formData.prizes.join(", ") : ""}
          onChange={(e) => setFormData({ ...formData, prizes: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
          sx={{ mt: 2 }}
          error={!!fieldErrors.prizes}
          helperText={fieldErrors.prizes}
        />
        <FormControl fullWidth margin="dense" sx={{ mt: 2 }} error={!!fieldErrors.area_ids}>
          <InputLabel id="area-ids-label">Award Areas</InputLabel>
          <Select
            labelId="area-ids-label"
            multiple
            value={formData.area_ids}
            onChange={(e) => setFormData({ ...formData, area_ids: e.target.value as number[] })}
            renderValue={(selected) =>
              awardAreas
                .filter((area) => selected.includes(area.id))
                .map((area) => area.name)
                .join(", ")
            }
          >
            {awardAreas.map((area) => (
              <MenuItem key={area.id} value={area.id}>
                <Checkbox checked={formData.area_ids.indexOf(area.id) > -1} />
                <ListItemText primary={area.name} />
              </MenuItem>
            ))}
          </Select>
          {fieldErrors.area_ids && (
            <p style={{ color: '#d32f2f', margin: '3px 14px 0 14px', fontSize: '0.75rem' }}>{fieldErrors.area_ids}</p>
          )}
        </FormControl>
        <TextField
          fullWidth
          margin="dense"
          label="Nomination Deadline"
          type="date"
          value={formData.nomination_deadline?.slice(0, 10) || ""}
          onChange={(e) => setFormData({ ...formData, nomination_deadline: e.target.value })}
          InputLabelProps={{ shrink: true }}
          error={!!fieldErrors.nomination_deadline}
          helperText={fieldErrors.nomination_deadline}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Ceremony Date"
          type="date"
          value={formData.ceremony_date?.slice(0, 10) || ""}
          onChange={(e) => setFormData({ ...formData, ceremony_date: e.target.value })}
          InputLabelProps={{ shrink: true }}
          error={!!fieldErrors.ceremony_date}
          helperText={fieldErrors.ceremony_date}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Ceremony Location"
          value={formData.ceremony_location}
          onChange={(e) => setFormData({ ...formData, ceremony_location: e.target.value })}
          error={!!fieldErrors.ceremony_location}
          helperText={fieldErrors.ceremony_location}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Nomination Period Start"
          type="date"
          value={formData.nomination_period_start?.slice(0, 10) || ""}
          onChange={(e) => setFormData({ ...formData, nomination_period_start: e.target.value })}
          InputLabelProps={{ shrink: true }}
          error={!!fieldErrors.nomination_period_start}
          helperText={fieldErrors.nomination_period_start}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Nomination Period End"
          type="date"
          value={formData.nomination_period_end?.slice(0, 10) || ""}
          onChange={(e) => setFormData({ ...formData, nomination_period_end: e.target.value })}
          InputLabelProps={{ shrink: true }}
          error={!!fieldErrors.nomination_period_end}
          helperText={fieldErrors.nomination_period_end}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Voting Period Start"
          type="date"
          value={formData.voting_period_start?.slice(0, 10) || ""}
          onChange={(e) => setFormData({ ...formData, voting_period_start: e.target.value })}
          InputLabelProps={{ shrink: true }}
          error={!!fieldErrors.voting_period_start}
          helperText={fieldErrors.voting_period_start}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Voting Period End"
          type="date"
          value={formData.voting_period_end?.slice(0, 10) || ""}
          onChange={(e) => setFormData({ ...formData, voting_period_end: e.target.value })}
          InputLabelProps={{ shrink: true }}
          error={!!fieldErrors.voting_period_end}
          helperText={fieldErrors.voting_period_end}
        />
        
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Images
          </Typography>
          
          {/* Existing Images */}
          {existingImages.length > 0 && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {existingImages.map((imageUrl, index) => (
                <Grid item key={`existing-${index}`}>
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <img
                      src={imageUrl}
                      alt={`Existing ${index + 1}`}
                      style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px' }}
                    />
                    <IconButton
                      onClick={() => handleRemoveExistingImage(index)}
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        backgroundColor: 'error.main',
                        color: 'white',
                        '&:hover': { backgroundColor: 'error.dark' },
                        width: 24,
                        height: 24
                      }}
                      size="small"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
          
          {/* New Image Previews */}
          {imageFiles.length > 0 && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {imageFiles.map((file, index) => (
                <Grid item key={`new-${index}`}>
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Preview ${index + 1}`}
                      style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px' }}
                    />
                    <IconButton
                      onClick={() => handleRemoveNewImage(index)}
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        backgroundColor: 'error.main',
                        color: 'white',
                        '&:hover': { backgroundColor: 'error.dark' },
                        width: 24,
                        height: 24
                      }}
                      size="small"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
          
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUpload />}
            sx={{ mt: 1 }}
          >
            Upload Images
            <input
              type="file"
              hidden
              accept="image/*"
              multiple
              onChange={handleImageChange}
            />
          </Button>
        </Box>
        
        {submissionError && (
          <Alert severity="error" onClose={handleCloseError} sx={{ mt: 2 }}>
            {submissionError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setModalOpen(false)} disabled={isSubmitting}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditAward; 